import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Helmet } from "react-helmet-async";
import logo from "@/assets/logo-fd-round.jpeg";

type AuthorizationDetails = {
  client?: { name?: string; redirect_uri?: string } | null;
  redirect_url?: string | null;
  redirect_to?: string | null;
  scopes?: string[];
};

export default function OAuthConsent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authorizationId = searchParams.get("authorization_id") ?? "";
  const [details, setDetails] = useState<AuthorizationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!authorizationId) {
        setError("Missing authorization request.");
        return;
      }
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        const next = window.location.pathname + window.location.search;
        window.location.href = `/admin?next=${encodeURIComponent(next)}`;
        return;
      }
      setEmail(sess.session.user.email ?? null);
      const { data, error: dErr } = await supabase.auth.oauth.getAuthorizationDetails(authorizationId);
      if (!active) return;
      if (dErr) return setError(dErr.message);
      if (data && "redirect_url" in data && typeof data.redirect_url === "string") {
        window.location.href = data.redirect_url;
        return;
      }
      setDetails(data as AuthorizationDetails);
    })();
    return () => {
      active = false;
    };
  }, [authorizationId]);

  async function decide(approve: boolean) {
    setBusy(true);
    const { data, error: dErr } = approve
      ? await supabase.auth.oauth.approveAuthorization(authorizationId)
      : await supabase.auth.oauth.denyAuthorization(authorizationId);
    if (dErr) {
      setBusy(false);
      return setError(dErr.message);
    }
    const target = data?.redirect_url;
    if (!target) {
      setBusy(false);
      return setError("No redirect returned by the authorization server.");
    }
    window.location.href = target;
  }

  const clientName = details?.client?.name ?? "an external app";
  const redirectHost = details?.client?.redirect_uri
    ? new URL(details.client.redirect_uri).host
    : "unknown host";

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Helmet>
          <title>Authorization Error | Federico Duronio</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <Card className="w-full max-w-md p-8 text-center space-y-4">
          <img src={logo} alt="Federico Duronio" className="w-16 h-16 rounded-full object-cover mx-auto" />
          <h1 className="text-xl font-bold text-foreground">Could not process request</h1>
          <p className="text-muted-foreground">{error}</p>
          <Button variant="outline" className="w-full" onClick={() => navigate("/home")}>
            Back to site
          </Button>
        </Card>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Helmet>
          <title>Authorization | Federico Duronio</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <Card className="w-full max-w-md p-8 text-center">
          <p className="text-muted-foreground">Loading authorization request…</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Helmet>
        <title>Connect {clientName} | Federico Duronio</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Card className="w-full max-w-md p-8 space-y-6">
        <div className="flex flex-col items-center gap-4">
          <img src={logo} alt="Federico Duronio" className="w-16 h-16 rounded-full object-cover" />
          <h1 className="text-xl font-bold text-foreground text-center">
            Connect {clientName} to Federico Duronio
          </h1>
        </div>

        <div className="space-y-3 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">{clientName}</strong> wants to use this app as you.
          </p>
          <p>Signed in as: <strong className="text-foreground">{email ?? "your account"}</strong></p>
          <p>Redirect destination: <code className="text-foreground">{redirectHost}</code></p>
          <p>
            This does not bypass the app&apos;s permissions or backend policies. Only approved tools will
            be available.
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" disabled={busy} onClick={() => decide(false)}>
            Cancel
          </Button>
          <Button variant="premium" className="flex-1" disabled={busy} onClick={() => decide(true)}>
            {busy ? "Processing…" : "Approve"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
